import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Paintbrush, Droplets, Wrench, Sparkles, DoorOpen, Layers, Hammer, CheckCircle, Package, MoreHorizontal } from 'lucide-react';

const SERVICES_IMG = 'https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/8zr6rlid_Presentacio%CC%81n%20-%20Ready2Rent%20%281700%20x%201080%20px%29%20%281500%20x%201080%20px%29%20%281300%20x%201080%20px%29.png';

const services = [
  { icon: Paintbrush, title: 'Pintura y saneamiento completo' },
  { icon: Droplets, title: 'Fragües y siliconas' },
  { icon: Wrench, title: 'Cambio y ajustes de chapas, tuercas y grifería' },
  { icon: Sparkles, title: 'Aseo completo' },
  { icon: DoorOpen, title: 'Reparación o cambio de bisagras' },
  { icon: Layers, title: 'Cambios de pisos' },
  { icon: Hammer, title: 'Carpintería y reparación de mobiliario' },
  { icon: CheckCircle, title: 'Terminaciones' },
  { icon: Package, title: 'Reposición de accesorios' },
  { icon: MoreHorizontal, title: 'Y mucho más' },
];

export default function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section
      id="servicios"
      data-testid="services-section"
      ref={ref}
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="font-manrope text-xs font-bold uppercase tracking-[0.2em] text-r2r-sage mb-4 block">
            Nuestros servicios
          </span>
          <h2
            data-testid="services-heading"
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-r2r-text"
          >
            Lo que hacemos
          </h2>
        </motion.div>

        {/* Main layout: Image + Services list */}
        <div className="grid lg:grid-cols-2 gap-0 border border-r2r-border">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="border-b lg:border-b-0 lg:border-r border-r2r-border overflow-hidden"
          >
            <img
              src={SERVICES_IMG}
              alt="Equipo Ready2Rent trabajando"
              data-testid="services-image"
              className="w-full h-full object-cover min-h-[300px]"
            />
          </motion.div>

          {/* Services list */}
          <div className="grid grid-cols-2">
            {services.map((svc, i) => (
              <motion.div
                key={svc.title}
                initial={{ opacity: 0, y: 12 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.06 }}
                data-testid={`service-card-${i}`}
                className={`flex items-start gap-3 p-5 lg:p-6 group hover:bg-r2r-bg transition-colors ${
                  i < services.length - 2 ? 'border-b border-r2r-border' : ''
                } ${i % 2 === 0 ? 'border-r border-r2r-border' : ''}`}
              >
                <svc.icon
                  size={18}
                  strokeWidth={1.5}
                  className="text-r2r-sage mt-0.5 shrink-0 group-hover:text-r2r-wood transition-colors"
                />
                <span className="font-manrope text-sm font-medium text-r2r-text leading-snug">
                  {svc.title}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-0 grid grid-cols-3 border border-t-0 border-r2r-border divide-x divide-r2r-border"
        >
          {[
            { value: '1.000+', label: 'Departamentos renovados' },
            { value: '48hrs', label: 'Tiempo promedio de entrega' },
            { value: '100%', label: 'Calidad garantizada' },
          ].map((stat, i) => (
            <div
              key={stat.label}
              data-testid={`stat-${i}`}
              className="p-6 lg:p-8 text-center"
            >
              <span className="font-outfit text-2xl lg:text-3xl font-extrabold text-r2r-text tracking-tighter">
                {stat.value}
              </span>
              <p className="font-manrope text-xs text-r2r-text-secondary mt-1">
                {stat.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
